import { Form } from "@circle-react-uikit/Form";
import { ModalManager } from "@circle-react/components/Layout/ModalManagerProvider";
import type { PostForQuickPost } from "@circle-react/components/QuickPost/types";
import { PostMomentumModal } from "@circle-react/components/QuickPostV2/PostMomentumModal";
import { QUICK_POST_V3_FORM_ID } from "@circle-react/components/QuickPostV3/constants";
import { QuickPostV3Content } from "@circle-react/components/QuickPostV3/Content";
import {
  buildPostFormPayload,
  createOrUpdateQuickPostV3,
} from "@circle-react/components/QuickPostV3/data/postSettingsFields";
import type { PostFormValues } from "@circle-react/components/QuickPostV3/data/types";
import { resetPersistedDraft } from "@circle-react/components/QuickPostV3/data/usePersistedDraft";
import { useComposerSpace } from "@circle-react/components/QuickPostV3/hooks/useComposerSpace";
import { QuickPostV3Settings } from "@circle-react/components/QuickPostV3/Settings";
import { useCurrentPostContext } from "@circle-react/contexts/currentPostContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { cssAnchorProps } from "@circle-react/helpers/cssAnchorHelpers";
import { afterSaveRedirectPath } from "@circle-react/helpers/postHelpers";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { commentRemovals } from "@circle-react/lib/queries/comments";
import { Modal } from "@circleco/compass/components/Modal";
import { useNavigate } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import type { ApiError } from "@/react/config/CustomErrors";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";

import { FormStateBridge } from "./FormStateBridge";
import { useQuickPostV3ModalStore } from "./store";
import { tiptapValidationSchema, trixValidationSchema } from "./validation";
import { QuickPostV3ViewTransition } from "./ViewTransition";

const modalHeightWithCover = 720;
const modalHeightWithoutCover = 600;
const heightTransitionMs = 220;
const heightTransitionEasing = "cubic-bezier(0.32, 0.72, 0, 1)";

interface QuickPostV3FormProps {
  defaultValues: PostFormValues;
  onForceClose: () => void;
  onRequestClose: () => void;
  onSubmitSuccess?: () => void;
  shouldPersistDraft?: boolean;
  shouldShowTipTap: boolean;
  shouldSkipRedirectAfterSubmit?: boolean;
  variant: "create" | "edit";
}

interface QuickPostFormModalContentProps {
  children: ReactNode;
  isFullscreen: boolean;
  isSmScreen: boolean;
  shouldUseFixedModalHeight: boolean;
}

const QuickPostFormModalContent = ({
  children,
  isFullscreen,
  isSmScreen,
  shouldUseFixedModalHeight,
}: QuickPostFormModalContentProps) => {
  const { hasCover } = useComposerSpace();
  const modalContentStyle: CSSProperties = {
    transitionDuration: `${heightTransitionMs}ms`,
    transitionTimingFunction: heightTransitionEasing,
    ...(shouldUseFixedModalHeight && {
      height: hasCover ? modalHeightWithCover : modalHeightWithoutCover,
    }),
  };
  const modalContentStyleProps = { style: modalContentStyle };

  return (
    <Modal.Content
      {...modalContentStyleProps}
      data-testid={
        isFullscreen && !isSmScreen
          ? "quick-post-v3-modal-content-fullscreen"
          : "quick-post-v3-modal-content"
      }
    >
      {children}
    </Modal.Content>
  );
};

const postStatusMessages: Record<string, () => string> = {
  draft: () => t("quick_post.toasts.draft_saved"),
  published: () => t("quick_post.toasts.published_saved"),
  scheduled: () => t("quick_post.toasts.scheduled_saved"),
};

const showSaveToast = ({
  isAdmin,
  isEdit,
  responseData,
  success,
}: {
  isAdmin: boolean;
  isEdit: boolean;
  responseData: PostForQuickPost;
  success: (msg: string) => void;
}) => {
  if (isEdit) {
    success(t("quick_post.toasts.changes_saved"));
    return;
  }
  if (isAdmin && responseData.status === "published") {
    void ModalManager.show(PostMomentumModal, { post: responseData });
    return;
  }
  success(postStatusMessages[responseData.status]());
};

export const QuickPostV3Form = ({
  defaultValues,
  onForceClose,
  onRequestClose,
  onSubmitSuccess,
  shouldPersistDraft,
  shouldSkipRedirectAfterSubmit,
  shouldShowTipTap,
  variant,
}: QuickPostV3FormProps) => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { currentCommunityMember } = usePunditUserContext();
  const { onPostChange, refetchPostDetails } = useCurrentPostContext();
  const isFullscreen = useQuickPostV3ModalStore(state => state.isFullscreen);
  const isSmScreen = useSmScreenMediaQuery();
  const isEdit = variant === "edit";
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  const savePostMutation = useMutation<
    PostForQuickPost,
    ApiError,
    PostFormValues
  >({
    mutationFn: async formData => {
      const response = await createOrUpdateQuickPostV3(
        buildPostFormPayload(formData, isEdit, defaultValues),
      );
      return (response as Response).json() as Promise<PostForQuickPost>;
    },
    onSuccess: (responseData, formData) => {
      const didToggleCommentsClosed =
        isEdit &&
        Boolean(formData.is_comments_closed) !==
          Boolean(defaultValues.is_comments_closed);

      onPostChange(responseData);
      refetchPostDetails?.();
      onSubmitSuccess?.();
      if (!isEdit) resetPersistedDraft();
      if (didToggleCommentsClosed) {
        // Refetch comments so per-comment `can_edit` reflects the new closed state.
        commentRemovals.forPost({ postId: responseData.id });
      }
      onForceClose();
      showSaveToast({ isAdmin, isEdit, responseData, success });
      if (!shouldSkipRedirectAfterSubmit) {
        void navigate({ href: afterSaveRedirectPath({ post: responseData }) });
      }
    },
    onError: () => {
      error(
        isEdit
          ? t("quick_post.toasts.failed_to_update")
          : t("quick_post.toasts.failed_to_create"),
      );
    },
  });

  const shouldUseFixedModalHeight = !isFullscreen && !isSmScreen;

  return (
    <Form<PostFormValues>
      id={QUICK_POST_V3_FORM_ID}
      defaultValues={defaultValues}
      mode="all"
      className="flex size-full min-h-0 min-w-0 flex-1 flex-col"
      formClassName="flex h-full min-h-0 flex-1 flex-col"
      onSubmit={async data => {
        await savePostMutation.mutateAsync(data);
      }}
      validationSchema={
        shouldShowTipTap ? tiptapValidationSchema : trixValidationSchema
      }
      {...cssAnchorProps({ anchor: "post-composer" })}
    >
      <QuickPostFormModalContent
        isFullscreen={isFullscreen}
        isSmScreen={isSmScreen}
        shouldUseFixedModalHeight={shouldUseFixedModalHeight}
      >
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          <FormStateBridge />
          <QuickPostV3ViewTransition
            composer={
              <QuickPostV3Content
                onRequestClose={onRequestClose}
                shouldPersistDraft={shouldPersistDraft}
                shouldShowTipTap={shouldShowTipTap}
                variant={variant}
              />
            }
            settings={<QuickPostV3Settings />}
          />
        </div>
      </QuickPostFormModalContent>
    </Form>
  );
};
